import { MultiSelectCheckboxTag, Info, Toastify } from 'lift-ui';
import React, { useState, useEffect } from 'react';

import { AlertFormData } from '../types';
import { SlackUser, SlackChannelWithDetails, TeamUser } from '../utils/dmgAlertApiService';

interface CommunicationStepProps {
  formData: AlertFormData;
  selectedCommunicationMethods: string[];
  selectedSlackRecipients: string[];
  slackUsers: SlackUser[];
  slackChannelsWithDetails: SlackChannelWithDetails[];
  teamUsers: TeamUser[];
  onCommunicationMethodChange: any;
  onSlackRecipientChange: any;
  onEmailRecipientsChange?: any;
  onEmailCcRecipientsChange?: any;
  onLiftRecipientsChange?: any;
  userEmail?: string;
  userId?: number; // Reserved for future use
  isEditMode?: boolean; // Whether editing an existing alert
  isLoadingCommunicationData?: boolean; // Whether communication data is being loaded
}

export const CommunicationStep: React.FC<CommunicationStepProps> = ({
  formData,
  selectedCommunicationMethods,
  selectedSlackRecipients,
  slackUsers,
  slackChannelsWithDetails,
  teamUsers,
  onCommunicationMethodChange,
  onSlackRecipientChange,
  onEmailRecipientsChange,
  onEmailCcRecipientsChange,
  onLiftRecipientsChange,
  userEmail,
  isEditMode = false,
  isLoadingCommunicationData = false,
}) => {
  const [emailRecipients, setEmailRecipients] = useState<string[]>(formData.emailRecipients || []);
  const [emailCcRecipients, setEmailCcRecipients] = useState<string[]>(
    formData.emailCcRecipients || []
  );
  const [liftNotificationUserRecipients, setLiftNotificationUserRecipients] = useState<string[]>(
    formData.liftNotificationUserRecipients || []
  );
  const [createdEmailCcTags, setCreatedEmailCcTags] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [createdEmailTags, setCreatedEmailTags] = useState<Array<{ label: string; value: string }>>(
    []
  );
  const [hasInitializedDefaults, setHasInitializedDefaults] = useState(false);

  // Sync local state with formData when navigating back to this step
  useEffect(() => {
    setEmailRecipients(formData.emailRecipients || []);
    setEmailCcRecipients(formData.emailCcRecipients || []);
    setLiftNotificationUserRecipients(formData.liftNotificationUserRecipients || []);

    // When emailCcRecipients are loaded, extract created tags from JSON values that don't match teamUsers
    if (
      formData.emailCcRecipients &&
      formData.emailCcRecipients.length > 0 &&
      teamUsers.length > 0
    ) {
      const newCcTags: Array<{ label: string; value: string }> = [];
      formData.emailCcRecipients.forEach(val => {
        try {
          const parsed = JSON.parse(val);
          if (parsed.email) {
            const matchingUser = teamUsers.find(u => u.email === parsed.email);
            if (!matchingUser) {
              newCcTags.push({ label: parsed.email, value: parsed.email });
            }
          }
        } catch {
          const matchingUser = teamUsers.find(u => u.email === val);
          if (!matchingUser) {
            newCcTags.push({ label: val, value: val });
          }
        }
      });
      if (newCcTags.length > 0) {
        setCreatedEmailCcTags(prev => {
          const merged = [...prev];
          newCcTags.forEach(tag => {
            if (!merged.find(t => t.value === tag.value)) merged.push(tag);
          });
          return merged;
        });
      }
    }

    // When emailRecipients are loaded, extract created tags from JSON values that don't match teamUsers
    if (formData.emailRecipients && formData.emailRecipients.length > 0 && teamUsers.length > 0) {
      const newCreatedTags: Array<{ label: string; value: string }> = [];
      formData.emailRecipients.forEach(val => {
        try {
          const parsed = JSON.parse(val);
          if (parsed.email) {
            // Check if this email exists in teamUsers
            const matchingUser = teamUsers.find(u => u.email === parsed.email);
            if (!matchingUser) {
              // This is a created tag (email not in teamUsers)
              newCreatedTags.push({
                label: parsed.email,
                value: parsed.email,
              });
            }
          }
        } catch {
          // Not JSON, treat as created tag
          const matchingUser = teamUsers.find(u => u.email === val);
          if (!matchingUser) {
            newCreatedTags.push({
              label: val,
              value: val,
            });
          }
        }
      });
      if (newCreatedTags.length > 0) {
        setCreatedEmailTags(prev => {
          // Merge with existing, avoiding duplicates
          const merged = [...prev];
          newCreatedTags.forEach(tag => {
            if (!merged.find(t => t.value === tag.value)) {
              merged.push(tag);
            }
          });
          return merged;
        });
      }
    }
  }, [
    formData.emailRecipients,
    formData.emailCcRecipients,
    formData.liftNotificationUserRecipients,
    teamUsers,
  ]);

  // Add current user to email and slack recipients by default if they exist in the lists
  // Only run in CREATE mode, not in EDIT mode. Skip when re-entering step with existing data
  // so that the user's latest choices are preserved when navigating back from the next step.
  useEffect(() => {
    // Skip auto-adding current user if in edit mode
    if (isEditMode) return;

    // Only run once when component mounts and lists are loaded, and userEmail is available
    if (hasInitializedDefaults || !userEmail) return;

    // When re-entering step 3 (e.g. from step 4), formData already has the user's choices.
    // Don't run defaults so we don't overwrite or re-add; preserve latest values.
    const hasExistingRecipients =
      (formData.emailRecipients?.length ?? 0) > 0 ||
      (formData.liftNotificationUserRecipients?.length ?? 0) > 0;
    if (hasExistingRecipients) {
      setHasInitializedDefaults(true);
      return;
    }

    // Wait for lists to be loaded (at least one list should have data)
    if (teamUsers.length === 0 && slackUsers.length === 0) return;

    const updates: { email: boolean; slack: boolean; lift: boolean } = {
      email: false,
      slack: false,
      lift: false,
    };

    // Check if current user exists in teamUsers and add to email recipients
    if (teamUsers.length > 0) {
      const currentUserInTeam = teamUsers.find(user => {
        if (!user?.email || !userEmail) return false;
        return user.email.toLowerCase() === userEmail.toLowerCase();
      });

      if (currentUserInTeam) {
        const userValue = JSON.stringify({
          id: currentUserInTeam.id,
          name: currentUserInTeam.name,
          email: currentUserInTeam.email,
        });

        // Check if user is already in email recipients
        const isInEmailRecipients = emailRecipients.some(val => {
          if (!val || !userEmail) return false;
          try {
            const parsed = JSON.parse(val);
            return parsed.email?.toLowerCase() === userEmail.toLowerCase();
          } catch {
            return val.toLowerCase() === userEmail.toLowerCase();
          }
        });

        if (!isInEmailRecipients) {
          const updatedEmailRecipients = [...emailRecipients, userValue];
          setEmailRecipients(updatedEmailRecipients);
          onEmailRecipientsChange?.(updatedEmailRecipients);
          updates.email = true;
        }

        // Check if user is already in LIFT notification user recipients
        const isInLiftRecipients = liftNotificationUserRecipients.some(val => {
          if (!val || !userEmail) return false;
          try {
            const parsed = JSON.parse(val);
            return parsed.email?.toLowerCase() === userEmail.toLowerCase();
          } catch {
            return val.toLowerCase() === userEmail.toLowerCase();
          }
        });

        if (!isInLiftRecipients) {
          const updatedLiftRecipients = [...liftNotificationUserRecipients, userValue];
          setLiftNotificationUserRecipients(updatedLiftRecipients);
          onLiftRecipientsChange?.(updatedLiftRecipients);
          updates.lift = true;
        }
      }
    }

    // Check if current user exists in slackUsers and add to slack recipients
    if (slackUsers.length > 0) {
      const currentUserInSlack = slackUsers.find(user => {
        if (!user?.email || !userEmail) return false;
        return user.email.toLowerCase() === userEmail.toLowerCase();
      });

      if (currentUserInSlack) {
        // Look up the user in teamUsers by email to get the correct team user ID
        const teamUser = teamUsers.find(
          tu => tu.email?.toLowerCase() === currentUserInSlack.email?.toLowerCase()
        );
        const teamUserId = teamUser?.id ?? currentUserInSlack.id ?? null;
        const slackUserValue = JSON.stringify({
          type: 'user',
          id: currentUserInSlack.slack_id,
          userId: teamUserId,
          email: currentUserInSlack.email,
          name: currentUserInSlack.real_name || currentUserInSlack.name,
        });

        // Check if user is already in slack recipients
        const isInSlackRecipients = selectedSlackRecipients.some(val => {
          try {
            const parsed = JSON.parse(val);
            return parsed.type === 'user' && parsed.id === currentUserInSlack.slack_id;
          } catch {
            return false;
          }
        });

        if (!isInSlackRecipients) {
          const updatedSlackRecipients = [...selectedSlackRecipients, slackUserValue];
          onSlackRecipientChange?.(updatedSlackRecipients);
          updates.slack = true;
        }
      }
    }

    // Mark as initialized after checking (whether updates were made or not)
    setHasInitializedDefaults(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    teamUsers.length,
    slackUsers.length,
    userEmail,
    isEditMode,
    formData.emailRecipients?.length,
    formData.liftNotificationUserRecipients?.length,
  ]);

  // Email validation function
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Edit mode: no longer auto-add current user when lists are empty, so behavior matches
  // create mode and the user can remove all recipients (validation will require at least
  // one per selected method before allowing Next).

  return (
    <div
      className="bg-white rounded-lg p-6 space-y-6 border border-black/20"
      style={{ minHeight: 'calc(90vh - 7rem)' }}
    >
      <div className="w-full max-w-md space-y-3">
        <div className={isLoadingCommunicationData ? 'opacity-50 pointer-events-none' : ''}>
          <MultiSelectCheckboxTag
            label="Select mode of communication"
            placeholder="Select communication methods"
            value={selectedCommunicationMethods}
            onChange={isLoadingCommunicationData ? () => {} : onCommunicationMethodChange}
            options={formData.communicationMethods.map(method => ({
              label: method.type,
              value: method.id,
            }))}
            containerClassName="w-[28rem]"
            required
            showSelectAll={false}
          />
        </div>
        {/* Info about LIFT notification mutual exclusivity */}
        {formData.communicationMethods.some(
          m =>
            m.type === 'LIFT notifications for a Brand' ||
            m.type === 'LIFT notifications for a user'
        ) && (
          <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-md">
            <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800">
              <span className="font-medium">Note:</span> You can only select either "LIFT
              notifications for a Brand" or "LIFT notifications for a user", not both. Selecting one
              will automatically deselect the other.
            </p>
          </div>
        )}
      </div>

      {selectedCommunicationMethods.some(
        id => formData.communicationMethods.find(m => m.id === id)?.type === 'Slack'
      ) && (
        <div className="w-full max-w-md">
          {(() => {
            // Create options for Slack users and channels
            const slackOptions = [
              // Add users with prefix to distinguish them
              ...slackUsers.map(user => {
                const nameOnly = `${user.real_name || user.name}`;
                // Look up the user in teamUsers by email to get the correct team user ID
                const teamUser = teamUsers.find(
                  tu => tu.email?.toLowerCase() === user.email?.toLowerCase()
                );
                const teamUserId = teamUser?.id ?? user.id ?? null;
                return {
                  label: `${nameOnly} (${user.email})`,
                  value: JSON.stringify({
                    type: 'user',
                    id: user.slack_id,
                    userId: teamUserId,
                    email: user.email,
                    name: nameOnly,
                  }),
                };
              }),
              // Add channels with detailed information
              ...slackChannelsWithDetails.map(channel => {
                const nameOnly = `#${channel.name}`;
                return {
                  label: nameOnly,
                  value: JSON.stringify({ type: 'channel', id: channel.id, name: nameOnly }),
                };
              }),
            ];

            // Transform selectedSlackRecipients to match the format of options
            // This ensures that recipients from API response match with available options
            const transformedSlackRecipients = selectedSlackRecipients.map(recipientStr => {
              try {
                const parsed = JSON.parse(recipientStr);
                if (parsed && parsed.id && parsed.type) {
                  // Try to find matching option by ID
                  if (parsed.type === 'user') {
                    const matchingUser = slackUsers.find(u => u.slack_id === parsed.id);
                    if (matchingUser) {
                      const nameOnly = `${matchingUser.real_name || matchingUser.name}`;
                      // Look up the user in teamUsers by email to get the correct team user ID
                      const teamUser = teamUsers.find(
                        tu => tu.email?.toLowerCase() === matchingUser.email?.toLowerCase()
                      );
                      const teamUserId = teamUser?.id ?? matchingUser.id ?? parsed.userId ?? null;
                      return JSON.stringify({
                        type: 'user',
                        id: matchingUser.slack_id,
                        userId: teamUserId,
                        email: matchingUser.email,
                        name: nameOnly,
                      });
                    }
                  } else if (parsed.type === 'channel') {
                    const matchingChannel = slackChannelsWithDetails.find(c => c.id === parsed.id);
                    if (matchingChannel) {
                      const nameOnly = `#${matchingChannel.name}`;
                      return JSON.stringify({
                        type: 'channel',
                        id: matchingChannel.id,
                        name: nameOnly,
                      });
                    }
                  }
                }
                // If no match found, return original (might be a valid option not in current lists)
                return recipientStr;
              } catch {
                // Not JSON, return as is (legacy format)
                return recipientStr;
              }
            });

            // Sort options: selected items first (in selection order), then unselected items alphabetically
            const selectedSlackValues = new Set(transformedSlackRecipients);
            const sortedSlackOptions = (() => {
              const selected: typeof slackOptions = [];
              const unselected: typeof slackOptions = [];

              // First, add selected items in their selection order
              transformedSlackRecipients.forEach(recipientValue => {
                const matchingOption = slackOptions.find(opt => opt.value === recipientValue);
                if (matchingOption) {
                  selected.push(matchingOption);
                }
              });

              // Then, add unselected items
              slackOptions.forEach(option => {
                if (!selectedSlackValues.has(option.value)) {
                  unselected.push(option);
                }
              });

              // Sort unselected items alphabetically by label
              unselected.sort((a, b) => a.label.localeCompare(b.label));

              // Return selected items first (in their selection order), then unselected items
              return [...selected, ...unselected];
            })();

            return (
              <div className={isLoadingCommunicationData ? 'opacity-50 pointer-events-none' : ''}>
                <MultiSelectCheckboxTag
                  label="Select Slack users or channels"
                  placeholder="Select users/channels"
                  tagMaxCharacters={50}
                  value={transformedSlackRecipients}
                  onChange={isLoadingCommunicationData ? () => {} : onSlackRecipientChange}
                  options={sortedSlackOptions}
                  containerClassName="w-[28rem]"
                  required
                />
              </div>
            );
          })()}
        </div>
      )}

      {selectedCommunicationMethods.some(
        id => formData.communicationMethods.find(m => m.id === id)?.type === 'Email'
      ) && (
        <div className="w-full max-w-md">
          {(() => {
            // Create options that include both teamUsers and created email tags
            const emailOptions = [
              ...teamUsers.map(user => ({
                label: `${user.name} (${user.email})`,
                value: JSON.stringify({ id: user.id, name: user.name, email: user.email }),
              })),
              ...createdEmailTags,
            ];

            // Transform emailRecipients to ensure they match the format expected by the component
            // The key is to match values with options by email address
            const transformedEmailRecipients = emailRecipients.map(val => {
              try {
                const parsed = JSON.parse(val);
                // If it's a valid JSON with email, try to find matching user in teamUsers
                if (parsed.email) {
                  const matchingUser = teamUsers.find(u => u.email === parsed.email);
                  if (matchingUser) {
                    // Use the exact format from options to ensure match
                    return JSON.stringify({
                      id: matchingUser.id,
                      name: matchingUser.name,
                      email: matchingUser.email,
                    });
                  }
                  // If no match in teamUsers, check if it's in createdEmailTags
                  const matchingTag = createdEmailTags.find(
                    tag => tag.value === parsed.email || tag.label === parsed.email
                  );
                  if (matchingTag) {
                    return matchingTag.value;
                  }
                  // Otherwise, keep the JSON format but ensure it has the right structure
                  return JSON.stringify({
                    id: parsed.id || null,
                    name: parsed.name || 'User',
                    email: parsed.email,
                  });
                }
                return val;
              } catch {
                // If it's not JSON, it's likely a plain email string
                // Check if it exists in teamUsers, if so create proper format
                // If it's not JSON, it's likely a plain email string
                // Check if it exists in teamUsers, if so create proper format
                const matchingUser = teamUsers.find(u => u.email === val);
                if (matchingUser) {
                  return JSON.stringify({
                    id: matchingUser.id,
                    name: matchingUser.name,
                    email: matchingUser.email,
                  });
                }
                // Check if it's in createdEmailTags
                const matchingTag = createdEmailTags.find(
                  tag => tag.value === val || tag.label === val
                );
                if (matchingTag) {
                  return matchingTag.value;
                }
                // Otherwise, it's a created tag (plain email) - return as is
                return val;
              }
            });

            // Sort options: selected items first (in selection order), then unselected items alphabetically
            const selectedEmailValues = new Set(transformedEmailRecipients);
            const sortedEmailOptions = (() => {
              const selected: typeof emailOptions = [];
              const unselected: typeof emailOptions = [];

              // First, add selected items in their selection order
              transformedEmailRecipients.forEach(recipientValue => {
                const matchingOption = emailOptions.find(opt => opt.value === recipientValue);
                if (matchingOption) {
                  selected.push(matchingOption);
                }
              });

              // Then, add unselected items
              emailOptions.forEach(option => {
                if (!selectedEmailValues.has(option.value)) {
                  unselected.push(option);
                }
              });

              // Sort unselected items alphabetically by label
              unselected.sort((a, b) => a.label.localeCompare(b.label));

              // Return selected items first (in their selection order), then unselected items
              return [...selected, ...unselected];
            })();

            return (
              <div className={isLoadingCommunicationData ? 'opacity-50 pointer-events-none' : ''}>
                <MultiSelectCheckboxTag
                  label="Select Email Users"
                  placeholder="Select users"
                  value={transformedEmailRecipients}
                  tagMaxCharacters={50}
                  onChange={
                    isLoadingCommunicationData
                      ? () => {}
                      : (values: string[]) => {
                          setEmailRecipients(values);
                          // Extract email addresses from values (could be JSON strings or plain emails)
                          onEmailRecipientsChange?.(values);
                        }
                  }
                  options={sortedEmailOptions}
                  {...({
                    creatable: !isLoadingCommunicationData,
                    onCreateOption: isLoadingCommunicationData
                      ? undefined
                      : (label: string) => {
                          const trimmedLabel = label.trim();

                          // Validate: empty check
                          if (!trimmedLabel) {
                            Toastify('error', 'Email cannot be empty');
                            return undefined;
                          }

                          // Validate: email format
                          if (!isValidEmail(trimmedLabel)) {
                            Toastify('error', 'Please enter a valid email address');
                            return undefined;
                          }

                          // Validate: check for duplicates in existing emailRecipients
                          const isDuplicate = emailRecipients.some(val => {
                            try {
                              const parsed = JSON.parse(val);
                              return parsed.email?.toLowerCase() === trimmedLabel.toLowerCase();
                            } catch {
                              return val.toLowerCase() === trimmedLabel.toLowerCase();
                            }
                          });

                          if (isDuplicate) {
                            Toastify('error', 'This email is already added');
                            return undefined;
                          }

                          // Validate: check if email exists in teamUsers (should use dropdown instead)
                          const existsInTeamUsers = teamUsers.some(
                            user => user.email?.toLowerCase() === trimmedLabel.toLowerCase()
                          );
                          if (existsInTeamUsers) {
                            Toastify(
                              'error',
                              'This email already exists in the user list. Please select from the dropdown.'
                            );
                            return undefined;
                          }

                          // Create new option with email as both label and value
                          const newValue = trimmedLabel;
                          const newOption = { label: newValue, value: newValue };

                          // Add to createdEmailTags if not already present
                          setCreatedEmailTags(prev => {
                            if (!prev.find(tag => tag.value === newValue)) {
                              return [...prev, newOption];
                            }
                            return prev;
                          });

                          return newOption;
                        },
                  } as Record<string, unknown>)}
                  required
                  containerClassName="w-full"
                />
              </div>
            );
          })()}

          {/* CC Email Users - optional when Email is selected */}
          {(() => {
            const emailCcOptions = [
              ...teamUsers.map(user => ({
                label: `${user.name} (${user.email})`,
                value: JSON.stringify({ id: user.id, name: user.name, email: user.email }),
              })),
              ...createdEmailCcTags,
            ];

            const transformedEmailCcRecipients = emailCcRecipients.map(val => {
              try {
                const parsed = JSON.parse(val);
                if (parsed?.email) {
                  const matchingUser = teamUsers.find(u => u.email === parsed.email);
                  if (matchingUser) {
                    return JSON.stringify({
                      id: matchingUser.id,
                      name: matchingUser.name,
                      email: matchingUser.email,
                    });
                  }
                  const matchingTag = createdEmailCcTags.find(
                    tag => tag.value === parsed.email || tag.label === parsed.email
                  );
                  if (matchingTag) return matchingTag.value;
                  return JSON.stringify({
                    id: parsed.id ?? null,
                    name: parsed.name ?? 'User',
                    email: parsed.email,
                  });
                }
                return val;
              } catch {
                const matchingUser = teamUsers.find(u => u.email === val);
                if (matchingUser) {
                  return JSON.stringify({
                    id: matchingUser.id,
                    name: matchingUser.name,
                    email: matchingUser.email,
                  });
                }
                const matchingTag = createdEmailCcTags.find(
                  tag => tag.value === val || tag.label === val
                );
                if (matchingTag) return matchingTag.value;
                return val;
              }
            });

            const selectedEmailCcValues = new Set(transformedEmailCcRecipients);
            const sortedEmailCcOptions = (() => {
              const selected: typeof emailCcOptions = [];
              const unselected: typeof emailCcOptions = [];
              transformedEmailCcRecipients.forEach(recipientValue => {
                const matchingOption = emailCcOptions.find(opt => opt.value === recipientValue);
                if (matchingOption) selected.push(matchingOption);
              });
              emailCcOptions.forEach(option => {
                if (!selectedEmailCcValues.has(option.value)) unselected.push(option);
              });
              unselected.sort((a, b) => a.label.localeCompare(b.label));
              return [...selected, ...unselected];
            })();

            return (
              <div
                className={isLoadingCommunicationData ? 'opacity-50 pointer-events-none' : ''}
                style={{ marginTop: '1rem' }}
              >
                <MultiSelectCheckboxTag
                  label="Select CC Email Users (optional)"
                  placeholder="Select users to CC"
                  value={transformedEmailCcRecipients}
                  tagMaxCharacters={50}
                  onChange={
                    isLoadingCommunicationData
                      ? () => {}
                      : (values: string[]) => {
                          setEmailCcRecipients(values);
                          onEmailCcRecipientsChange?.(values);
                        }
                  }
                  options={sortedEmailCcOptions}
                  {...({
                    creatable: !isLoadingCommunicationData,
                    onCreateOption: isLoadingCommunicationData
                      ? undefined
                      : (label: string) => {
                          const trimmedLabel = label.trim();
                          if (!trimmedLabel) {
                            Toastify('error', 'Email cannot be empty');
                            return undefined;
                          }
                          if (!isValidEmail(trimmedLabel)) {
                            Toastify('error', 'Please enter a valid email address');
                            return undefined;
                          }
                          const isDuplicate = emailCcRecipients.some(val => {
                            try {
                              const parsed = JSON.parse(val);
                              return parsed.email?.toLowerCase() === trimmedLabel.toLowerCase();
                            } catch {
                              return val.toLowerCase() === trimmedLabel.toLowerCase();
                            }
                          });
                          if (isDuplicate) {
                            Toastify('error', 'This email is already added');
                            return undefined;
                          }
                          const existsInTeamUsers = teamUsers.some(
                            u => u.email?.toLowerCase() === trimmedLabel.toLowerCase()
                          );
                          if (existsInTeamUsers) {
                            Toastify(
                              'error',
                              'This email already exists in the user list. Please select from the dropdown.'
                            );
                            return undefined;
                          }
                          const newValue = trimmedLabel;
                          const newOption = { label: newValue, value: newValue };
                          setCreatedEmailCcTags(prev => {
                            if (!prev.find(tag => tag.value === newValue)) {
                              return [...prev, newOption];
                            }
                            return prev;
                          });
                          return newOption;
                        },
                  } as Record<string, unknown>)}
                  containerClassName="w-full"
                />
              </div>
            );
          })()}
        </div>
      )}

      {/* LIFT notifications for a Brand - no input needed, uses brand-level recipients */}
      {selectedCommunicationMethods.some(
        id =>
          formData.communicationMethods.find(m => m.id === id)?.type ===
          'LIFT notifications for a Brand'
      ) && (
        <div className="w-full max-w-md">
          <p className="text-sm text-gray-600">
            Brand-level notifications will be sent to all users associated with selected brands.
          </p>
        </div>
      )}

      {/* LIFT notifications for a user - same dropdown as Email */}
      {selectedCommunicationMethods.some(
        id =>
          formData.communicationMethods.find(m => m.id === id)?.type ===
          'LIFT notifications for a user'
      ) && (
        <div className="w-full max-w-md">
          {(() => {
            const liftOptions = teamUsers.map(user => ({
              label: `${user.name} (${user.email})`,
              // Store JSON so mapper can extract id/name/email
              value: JSON.stringify({ id: user.id, name: user.name, email: user.email }),
            }));

            // Sort options: selected items first (in selection order), then unselected items alphabetically
            const selectedLiftValues = new Set(liftNotificationUserRecipients);
            const sortedLiftOptions = (() => {
              const selected: typeof liftOptions = [];
              const unselected: typeof liftOptions = [];

              // First, add selected items in their selection order
              liftNotificationUserRecipients.forEach(recipientValue => {
                const matchingOption = liftOptions.find(opt => opt.value === recipientValue);
                if (matchingOption) {
                  selected.push(matchingOption);
                }
              });

              // Then, add unselected items
              liftOptions.forEach(option => {
                if (!selectedLiftValues.has(option.value)) {
                  unselected.push(option);
                }
              });

              // Sort unselected items alphabetically by label
              unselected.sort((a, b) => a.label.localeCompare(b.label));

              // Return selected items first (in their selection order), then unselected items
              return [...selected, ...unselected];
            })();

            return (
              <div className={isLoadingCommunicationData ? 'opacity-50 pointer-events-none' : ''}>
                <MultiSelectCheckboxTag
                  label="Select LIFT Notification Users"
                  placeholder="Select users"
                  value={liftNotificationUserRecipients}
                  tagMaxCharacters={50}
                  onChange={
                    isLoadingCommunicationData
                      ? () => {}
                      : (values: string[]) => {
                          setLiftNotificationUserRecipients(values);
                          onLiftRecipientsChange?.(values);
                        }
                  }
                  options={sortedLiftOptions}
                  required
                  containerClassName="w-[28rem]"
                />
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};
